import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between align-center" }
const _hoisted_2 = { class: "tw-label align-center" }
const _hoisted_3 = { class: "mt-4" }
const _hoisted_4 = ["id", "contenteditable"]

import orderBy from 'lodash/orderBy';
import { onMounted, ref, watch } from 'vue';
import CommonService from '@/backend/service/CommonService';
import FileService from '@/backend/service/FileService';
import NOT_FOUND_IMAGE from '@/assets/not-found.png';
import { useI18n } from 'vue-i18n';

export interface ISelectionData {
  start: number;
  end?: number;
  id?: string;
  url?: string;
  type?: string;
}

export interface ITextFormatPart extends ISelectionData {
  typeFormat: string;
  end: number;
  text: string;
}

export interface TWysiwygResponse {
  content: string;
  format: Record<string, ISelectionData[]>;
  media?: MediaFile[];
}

export type MediaFile = {
  name: string;
  size: number;
  type: string;
  id?: string;
  url?: string;
  serverFileName?: string;
};

type MediaBuilderData = {
  filePath: string;
  fileId: string;
  needPreloader?: boolean;
};

enum FORMAT_VARIANT {
  ITALIC = 'italic',
  BOLD = 'bold',
  UNDERLINE = 'underline',
  STRIKE = 'strikeThrough',
  SPOILER = 'spoiler',
  HTML = 'insertHtml',
  MEDIA = 'media',
  LINK = 'link'
}

enum MEDIA_TAGS {
  IMAGE = 'img',
  VIDEO = 'video',
  LINK = 'a'
}
enum MEDIA_TYPES {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO'
}

enum MEDIA_DOM_ATTRIBUTES {
  POSTER = 'poster'
}

const SPOILER_TAG = 'span';

const EMPTY_STRING = '';
const DEFAULT_DELAY = 250;
const NEW_LINE = '\n';

const SPOILER_CLASS = 'spoiler';

const MEDIA_FILE_PREFIX = 'media';
const MEDIA_MAX_FILESIZE = 104857600;//bytes


export default /*#__PURE__*/_defineComponent({
  __name: 'TWysiwyg',
  props: {
    label: {},
    content: {},
    useEditor: {}
  },
  emits: ['change'],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const emit = __emit;
const props = __props;

let activeFormatting = ref<string[]>([]);
let FROMATTING_BUTTONS = ref([
  { icon: 'mdi-format-italic', value: FORMAT_VARIANT.ITALIC, active: false },
  { icon: 'mdi-format-bold', value: FORMAT_VARIANT.BOLD, active: false },
  { icon: 'mdi-format-underline', value: FORMAT_VARIANT.UNDERLINE, active: false },
  { icon: 'mdi-format-strikethrough-variant', value: FORMAT_VARIANT.STRIKE, active: false },
  { icon: 'mdi-eye-off', value: FORMAT_VARIANT.SPOILER, active: false }
]);

const divEditable = ref<HTMLDivElement | null>(null);
const divId = Math.random().toString(36).substring(2, 10);

const linkDialog = ref({
  open: false,
  text: '',
  url: 'https://'
});

const mediaDialog = ref({
  open: false,
  file: null as unknown as MediaFile
});

let mediaData = [] as MediaFile[];

const isContentEditable = ref(props.useEditor as boolean);

const uploadMediaErrors = ref('');

let lastRange: Range | undefined = undefined;
let lastWindowSelection: Selection | null = null;

onMounted(() => {
  if (props.content && props.content.content.length > 0) {
    renderMessageAsHtml(props.content);
  }
  watch(
    () => activeFormatting.value,
    (newValue) => {
      FROMATTING_BUTTONS.value.map((fb) => {
        fb.active = newValue.includes(fb.value);
      });
    },
    {
      deep: true
    }
  );

  watch(
    () => linkDialog.value.open,
    (newValue) => {
      if (!newValue) {
        linkDialog.value.text = '';
        linkDialog.value.url = '';
      }
    },
    {
      deep: true
    }
  );

  watch(
    () => mediaDialog.value.open,
    (newValue) => {
      if (!newValue) {
        mediaDialog.value.file = {} as MediaFile;
        uploadMediaErrors.value = '';
      }
    },
    {
      deep: true
    }
  );
});

function sendStateContent() {
  const contentData = save();
  emit('change', contentData);
}

function save(): TWysiwygResponse {
  let formatSetting: Record<string, ISelectionData[]> = {};
  let findTagRegexp = /<[^<>]+>/;
  const clearHtmlTagRegexp = /<[a-z]*[^(a|i|b|u|strike|span)]>/g;
  let copyContent = divEditable.value!.innerHTML.slice(0);
  copyContent = copyContent.replaceAll(clearHtmlTagRegexp, '');
  const allTags = copyContent.match(new RegExp(findTagRegexp, 'g'))?.filter((tag) => tag.substring(0, 2) != '</').filter(tag => !tag.includes('video-')) ?? []; 
  
  allTags.forEach((findedTag: string) => {
    let result = copyContent.match(findTagRegexp);
    
    if (result != null) {
      const startTag = result[0];
      const startPosition = result.index as number;
      const isImage = findedTag.includes(`<${MEDIA_TAGS.IMAGE}`);
      const isVideo = findedTag.includes(`<${MEDIA_TAGS.VIDEO}`);
      const isLink = findedTag.includes(`<${MEDIA_TAGS.LINK}`);
      const isSpoiler = findedTag.includes(SPOILER_CLASS);

      if (isImage || isVideo) {
        // for img and video tags
        const parser = new DOMParser();

        const doc = parser.parseFromString(findedTag, 'text/html');

        const element: HTMLElement = doc.body.firstChild! as HTMLElement;
        const fileIdAttribute = element.getAttribute('id') ?? '';
        let formatSettingKey = FORMAT_VARIANT.MEDIA;
        const positionItem: ISelectionData = {
          start: startPosition,
          id: fileIdAttribute,
          type: isImage ? MEDIA_TYPES.IMAGE : MEDIA_TYPES.VIDEO
        };
        if (formatSettingKey in formatSetting) {
          formatSetting[formatSettingKey].push(positionItem);
        } else {
          formatSetting[formatSettingKey] = [positionItem];
        }

        if(isImage){
          copyContent = copyContent.replace(startTag, EMPTY_STRING);
        }else if(isVideo){
          const videoWrapperRegexp = new RegExp(`${startTag}.+?video></div>`, 'g');                    
          copyContent = copyContent.replace(videoWrapperRegexp, EMPTY_STRING);
        }
      } else {
        // for b, i, u, strike tags
        const startTagClear = isLink
          ? MEDIA_TAGS.LINK
          : isSpoiler
            ? SPOILER_TAG
            : startTag.replace('<', EMPTY_STRING).replace('>', EMPTY_STRING).split(' ')[0];
        const endTag = `</${startTagClear}>`;
        copyContent = copyContent.replace(startTag, EMPTY_STRING);
        let resultEnd = copyContent.substring(startPosition).match(new RegExp(endTag));
        let endPosition = startPosition + resultEnd?.index!;
        const insideContent = copyContent.substring(startPosition, endPosition).replace(/(<([^>]+)>)/gi, EMPTY_STRING);
        endPosition = startPosition + insideContent.length;
        copyContent = copyContent.replace(endTag, EMPTY_STRING);

        let formatSettingKey: string = EMPTY_STRING;
        switch (startTagClear) {
            case 'b':
              formatSettingKey = FORMAT_VARIANT.BOLD;
              break;
            case 'i':
              formatSettingKey = FORMAT_VARIANT.ITALIC;
              break;
            case 'u':
              formatSettingKey = FORMAT_VARIANT.UNDERLINE;
              break;
            case 'strike':
              formatSettingKey = FORMAT_VARIANT.STRIKE;
              break;
            case SPOILER_TAG:
              formatSettingKey = FORMAT_VARIANT.SPOILER;
              break;
            case MEDIA_TAGS.LINK:
              formatSettingKey = FORMAT_VARIANT.LINK;
              break;
        }

        if (formatSettingKey === EMPTY_STRING) return false;

        let positionItem: ISelectionData;
        if (isLink) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(startTag, 'text/html');
          const element: Element = doc.body.firstChild! as Element;
          const hrefData = element.getAttribute('href') ?? '';
          positionItem = { start: startPosition, end: endPosition, url: hrefData };
        } else {
          positionItem = { start: startPosition, end: endPosition };
        }
        if (formatSettingKey in formatSetting) {
          formatSetting[formatSettingKey].push(positionItem);
        } else {
          formatSetting[formatSettingKey] = [positionItem];
        }
      }
    }
  });
  const formattingMedia = formatSetting[FORMAT_VARIANT.MEDIA] ?? [];
  mediaData = mediaData.filter( mdItem => formattingMedia.findIndex(fmItem => fmItem.id === mdItem.id) > -1);
  const responseData: TWysiwygResponse = { content: copyContent, format: formatSetting, media: mediaData };
  return responseData;
}

function selectionOnActiveSpoiler(): boolean {
  let anchorParentClassList = ((lastWindowSelection?.anchorNode as HTMLElement).parentNode as Element).classList;
  let focusParentClassList = ((lastWindowSelection?.focusNode as HTMLElement).parentNode as Element).classList;
  return anchorParentClassList.contains(SPOILER_CLASS) || focusParentClassList.contains(SPOILER_CLASS);
}

function textFormatHandler(_formatVariant: FORMAT_VARIANT) {
  const beforeApply = activeFormatting.value.includes(_formatVariant);
  if (_formatVariant === FORMAT_VARIANT.SPOILER) {
    if (selectionOnActiveSpoiler()) {
      const elem = (lastWindowSelection?.anchorNode as HTMLElement).parentNode as HTMLElement;
      const text = elem.innerText ?? '';
      if (text?.length > 0) {
        elem.replaceWith(text);
      }
    } else {
      document.execCommand(FORMAT_VARIANT.HTML, false, buildSpoilerHtml(lastWindowSelection?.toString() as string));
    }
  } else {
    document.execCommand(_formatVariant);
  }

  if (beforeApply) {
    activeFormatting.value = activeFormatting.value.filter((format) => format != _formatVariant);
  } else {
    activeFormatting.value.push(_formatVariant);
  }
}

function updateRangeSelection() {
  let selection = window.getSelection();

  lastWindowSelection = selection;
  lastRange = selection?.getRangeAt(0);
}

function processSelection() {
  updateRangeSelection();
  activeFormatting.value = [];

  if (document.queryCommandState(FORMAT_VARIANT.BOLD)) {
    activeFormatting.value.push(FORMAT_VARIANT.BOLD);
  }
  if (document.queryCommandState(FORMAT_VARIANT.ITALIC)) {
    activeFormatting.value.push(FORMAT_VARIANT.ITALIC);
  }
  if (
    document.queryCommandState(FORMAT_VARIANT.UNDERLINE) &&
    lastRange?.commonAncestorContainer.parentElement?.localName !== MEDIA_TAGS.LINK
  ) {
    activeFormatting.value.push(FORMAT_VARIANT.UNDERLINE);
  }
  if (document.queryCommandState(FORMAT_VARIANT.STRIKE)) {
    activeFormatting.value.push(FORMAT_VARIANT.STRIKE);
  }
  if (selectionOnActiveSpoiler()) {
    activeFormatting.value.push(FORMAT_VARIANT.SPOILER);
  }
}

function processSelectionWithTimeout() {
  setTimeout(() => {
    processSelection();
  }, DEFAULT_DELAY);
}

function handleMouseDown(e: MouseEvent) {
  e.target?.addEventListener('mouseup', processSelectionWithTimeout, { once: true });
}

function insertLink() {
  const { url, text } = linkDialog.value;
  linkDialog.value.open = false;
  setTimeout(() => {
    const el = divEditable!.value!;
    el.focus();
    restoreCursorPosition();
    if (url.length > 0 && text.length > 0) {
      document.execCommand(FORMAT_VARIANT.HTML, false, buildLinkHtml(url, text));
    }
  }, DEFAULT_DELAY);
}

function buildSpoilerHtml(text: string) {
  return `<span class="${SPOILER_CLASS}">${text}</span>`;
}

function buildLinkHtml(url: string, text: string) {
  return `<a href="${url}" target="_blank">${text}</a>`;
}

function buildVideoHtml(data: MediaBuilderData) {
  return `<div data-wpid="${data.fileId}" class="video-wrapper"><div contenteditable="false" class="video-delete"><i class="video-delete__icon v-icon mdi mdi-delete-outline"></i></div><video ${data.needPreloader ? 'class="media-loading"' : ''} preload="metadata" controls id="${data.fileId}" src="${data.filePath}"></video></div>`;
}

function buildImageHtml(data: MediaBuilderData) {
  return `<p><img ${data.needPreloader ? 'class="media-loading"' : ''} id="${data.fileId}" src="${data.filePath}"/></p>`;
}

function insertMedia() {
  const currentFile = Object.assign({}, mediaDialog.value.file);
  const localFilePath = currentFile.url!;

  if (currentFile.id?.length == 0) return false;

  mediaDialog.value.open = false;
  const isVideo = currentFile.type === MEDIA_TYPES.VIDEO;
  const insertContent = isVideo
    ? buildVideoHtml({ filePath: localFilePath, fileId: currentFile.id! }) + NEW_LINE
    : buildImageHtml({ filePath: localFilePath, fileId: currentFile.id! });
  setTimeout(() => {
    const el = divEditable!.value!;
    el.focus();
    restoreCursorPosition();
    document.execCommand(FORMAT_VARIANT.HTML, false, insertContent);
    mediaDialog.value.file = {} as MediaFile;
    addVideoDeleteClickEvent();
  }, DEFAULT_DELAY);
}

function restoreCursorPosition() {
  const clonedRange = lastRange?.cloneRange();
  clonedRange?.selectNodeContents(divEditable.value as Node);
  clonedRange?.setStart(lastRange?.startContainer as Node, lastRange?.startOffset as number);
  clonedRange?.setEnd(lastRange?.endContainer as Node, lastRange?.endOffset as number);
  lastWindowSelection?.removeAllRanges();
  lastWindowSelection?.addRange(clonedRange!);
}

function selectMediaHandler(_files: File[]) {
  const f = _files[0];
  if(f === undefined){
    uploadMediaErrors.value = '';
    return false;
  } 
  if(f.size > MEDIA_MAX_FILESIZE){
    uploadMediaErrors.value = t('TextFieldError.MaxFileSize', { size: Math.round(MEDIA_MAX_FILESIZE/Math.pow(1024,2)) });
  }else{
    let formData = new FormData();
    formData.append('prefix', MEDIA_FILE_PREFIX);
    formData.append('file', f);
    CommonService.uploadFile(formData).then(
      (data: string) => {
        const fileId = data.replace(`${MEDIA_FILE_PREFIX}-`, '').split('.', 2)[0];
        const fileMediaType = f.type.includes(MEDIA_TYPES.IMAGE.toLowerCase()) ? MEDIA_TYPES.IMAGE : MEDIA_TYPES.VIDEO;
        const selectedFile: MediaFile = {
          name: f.name,
          size: f.size,
          type: fileMediaType,
          url: URL.createObjectURL(f),
          id: fileId,
          serverFileName: data
        };
        mediaDialog.value.file = selectedFile;
        mediaData.push(selectedFile);
      },
      () => {
      /**/
      }
    );
  }
}

function handlePaste(e: any) {
  e.preventDefault();
  const text = e.clipboardData ? (e.originalEvent || e).clipboardData.getData('text/plain') : '';
  document.execCommand(FORMAT_VARIANT.HTML, false, text);
}

function handleKeyDown(e: any) {
  if (e.key === 'Enter') {
    document.execCommand('insertLineBreak');
    e.preventDefault();
  }
}

function handleInput() {
  updateRangeSelection();
  sendStateContent();
}

function createTextFormatParts(message: string, formattingData: Record<string, ISelectionData[]>): ITextFormatPart[] {
  let parts: ITextFormatPart[] = [];
  for (let typeFormat in formattingData) {
    const typeFormatParts = formattingData[typeFormat];
    typeFormatParts.forEach((item: ISelectionData) => {
      parts.push({
        typeFormat: typeFormat == FORMAT_VARIANT.MEDIA ? (item.type as string) : typeFormat,
        start: item.start,
        end: item.end ?? item.start,
        text: message.substring(item.start, item.end),
        id: item.id
      });
    });
  }
  return parts;
}

function processMessageAsHtml(message: string, parts: ITextFormatPart[]): Promise<string> {
  return new Promise((resolve) => {
    parts.map((current) => {
      if (current.typeFormat.length === 0) return false;
      let forReplace = '';
      switch (current.typeFormat) {
          case FORMAT_VARIANT.BOLD:
            forReplace = `<b>${current.text}</b>`;
            break;
          case FORMAT_VARIANT.ITALIC:
            forReplace = `<i>${current.text}</i>`;
            break;
          case FORMAT_VARIANT.UNDERLINE:
            forReplace = `<u>${current.text}</u>`;
            break;
          case FORMAT_VARIANT.STRIKE:
            forReplace = `<strike>${current.text}</strike>`;
            break;
          case FORMAT_VARIANT.LINK:
            forReplace = buildLinkHtml(current.url!, current.text);
            break;
          case FORMAT_VARIANT.SPOILER:
            forReplace = buildSpoilerHtml(current.text);
            break;
      }
      let oneHalf = message.substring(0, current.start);
      let twoHalf = message.substring(current.start);
      if (forReplace.length > 0) {
        message = oneHalf + twoHalf.replace(current.text, forReplace);
      } else if (current.typeFormat == MEDIA_TYPES.VIDEO || current.typeFormat == MEDIA_TYPES.IMAGE) {
        const currentId = current!.id as string;
        if (currentId.length > 0) {
          FileService.getRemoteThumbnailAsBinary(currentId)
            .then((binaryData: ArrayBuffer) => {
              const url = window.URL.createObjectURL(new Blob([binaryData]));
              const elem = document.getElementById(currentId);
              if (current.typeFormat == MEDIA_TYPES.IMAGE) {
                elem?.setAttribute('src', url);
              } else if (current.typeFormat == MEDIA_TYPES.VIDEO) {
                elem?.setAttribute(MEDIA_DOM_ATTRIBUTES.POSTER, url);
              }
              elem?.classList.remove("media-loading");
            })
            .catch(() => {});

          if(current.typeFormat == MEDIA_TYPES.VIDEO){
            FileService.getRemoteFileAsBinary(currentId)
              .then((binaryData: ArrayBuffer) => {
                const url = window.URL.createObjectURL(new Blob([binaryData]));
                const elem = document.getElementById(currentId);
                elem?.setAttribute('src', url);
              })
              .catch(() => {
                const elem = document.getElementById(currentId);
                elem?.setAttribute(MEDIA_DOM_ATTRIBUTES.POSTER, NOT_FOUND_IMAGE);
              });
          }
        }
        const url = "none.webp";
        message =
          oneHalf +
          (current.typeFormat == MEDIA_TYPES.VIDEO
            ? buildVideoHtml({ filePath: url, fileId: currentId, needPreloader: true })
            : buildImageHtml({ filePath: url, fileId: currentId, needPreloader: true })) +
          twoHalf;
      }
    });
    resolve(message);
  });
}

async function renderMessageAsHtml(serverResponse: TWysiwygResponse) {
  // disassemble format and create it textParts<{type: typePart, start: number, text: string, path: string, id: string}>
  // insert tags into the main text, moving from the end to the beginning - this will allow us to maintain adequate starting positions
  let { content, format } = serverResponse;

  let parts: ITextFormatPart[] = createTextFormatParts(content, format);
  parts = orderBy(parts, ['start'], ['desc']);
  content = await processMessageAsHtml(content, parts);
  divEditable.value!.innerHTML = content;
  addVideoDeleteClickEvent();
}

function addVideoDeleteClickEvent(){
  const videos = Array.from(document.getElementsByClassName("video-delete__icon"));
  videos.forEach((item)=>{
    const clickEventName = 'click';
    item.removeEventListener(clickEventName, onClickDeleteVideo);
    item.addEventListener(clickEventName, onClickDeleteVideo);   
  });
}

function onClickDeleteVideo(e:Event){
  const elem = e.target as HTMLElement;
  const fileId = elem.getAttribute("data-fileid") ?? '';
  ((elem?.parentNode as HTMLElement).parentNode as HTMLElement).remove();
  mediaData = mediaData.filter((mediaItem)=>mediaItem.id != fileId);
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_btn_group = _resolveComponent("v-btn-group")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    fluid: "",
    class: "pa-0 ma-0 w-75"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_dialog, {
        modelValue: linkDialog.value.open,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((linkDialog.value.open) = $event)),
        width: "700"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('Modal.Wysiwyg.LinkDialog.Title')), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "pa-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: linkDialog.value.text,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((linkDialog.value.text) = $event)),
                    modelModifiers: { trim: true },
                    variant: "outlined",
                    color: "primary",
                    autocomplete: "off",
                    "auto-grow": "",
                    "persistent-placeholder": "",
                    "hide-details": "",
                    "bg-color": "input-background",
                    placeholder: _ctx.$t('TextField.Text')
                  }, null, 8, ["modelValue", "placeholder"]),
                  _createVNode(_component_v_text_field, {
                    modelValue: linkDialog.value.url,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((linkDialog.value.url) = $event)),
                    modelModifiers: { trim: true },
                    variant: "outlined",
                    color: "primary",
                    class: "mt-4",
                    autocomplete: "off",
                    "auto-grow": "",
                    "persistent-placeholder": "",
                    "hide-details": "",
                    "bg-color": "input-background",
                    placeholder: _ctx.$t('TextField.Url')
                  }, null, 8, ["modelValue", "placeholder"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "px-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    class: "px-4",
                    color: "error",
                    variant: "text",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (linkDialog.value.open = false))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('Button.Cancel')), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    class: "px-4",
                    color: "primary",
                    variant: "flat",
                    onClick: insertLink
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('Button.Save')), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: mediaDialog.value.open,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((mediaDialog.value.open) = $event)),
        width: "700"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('Modal.Wysiwyg.MediaDialog.Title')), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "pa-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_file_input, {
                    variant: "outlined",
                    color: "primary",
                    autocomplete: "off",
                    "persistent-placeholder": "",
                    "bg-color": "input-background",
                    "show-size": "",
                    accept: "image/*, video/*",
                    placeholder: _ctx.$t('TextField.File'),
                    "error-messages": uploadMediaErrors.value,
                    "onUpdate:modelValue": selectMediaHandler
                  }, null, 8, ["placeholder", "error-messages"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "px-4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    class: "px-4",
                    color: "error",
                    variant: "text",
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (mediaDialog.value.open = false))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('Button.Cancel')), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    class: "px-4",
                    color: "primary",
                    variant: "flat",
                    disabled: mediaDialog.value.file?.id == undefined,
                    onClick: insertMedia
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('Button.Save')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_1, [
        (_ctx.useEditor)
          ? (_openBlock(), _createBlock(_component_v_btn_group, {
              key: 0,
              class: "format-group",
              variant: "outlined",
              "bg-color": "input-background",
              rounded: ""
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(FROMATTING_BUTTONS), (button) => {
                  return (_openBlock(), _createBlock(_component_v_btn, {
                    key: button.value,
                    icon: button.icon,
                    value: button.value,
                    class: _normalizeClass({ 'v-btn--active': button.active }),
                    onClick: ($event: any) => (textFormatHandler(button.value))
                  }, null, 8, ["icon", "value", "class", "onClick"]))
                }), 128)),
                _createVNode(_component_v_btn, {
                  icon: "mdi-link",
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (linkDialog.value.open = true))
                }),
                _createVNode(_component_v_btn, {
                  icon: "mdi-video-image",
                  onClick: _cache[7] || (_cache[7] = ($event: any) => (mediaDialog.value.open = true))
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.label), 1)
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", {
          id: _unref(divId),
          ref_key: "divEditable",
          ref: divEditable,
          role: "textbox",
          class: "editable rounded",
          contenteditable: isContentEditable.value,
          onMousedown: handleMouseDown,
          onPaste: handlePaste,
          onInput: handleInput,
          onKeydown: handleKeyDown
        }, null, 40, _hoisted_4)
      ])
    ]),
    _: 1
  }))
}
}

})